import PropTypes from 'prop-types';
import React, { Component } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { Link } from 'react-router-dom';
import Grid from 'react-bootstrap/lib/Grid';
import Row from 'react-bootstrap/lib/Row';
import PricingFAQSection from 'components/Pricing/PricingFAQ/PricingFAQSection';

import styles from './PricingFAQ.module.scss';

export default class PricingBuyerFAQ extends Component {
  static propTypes = {
    className: PropTypes.string,
    gradient: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf(['left', 'right']),
    ]),
  };

  static defaultProps = {
    gradient: false,
  };

  constructor() {
    super();

    this.state = {
      visible: false,
    };
  }

  render() {
    const { className, gradient, ...rest } = this.props;

    const { visible } = this.state;

    return (
      <VisibilitySensor
        partialVisibility
        delayedCall
        minTopValue={visible ? 0 : 100}
        active={!visible}
        onChange={(isVisible) => this.setState({ visible: isVisible })}
      >
        <div
          className={
            (gradient
              ? styles.gradient +
                ' ' +
                (gradient === 'left'
                  ? styles.gradientLeft
                  : styles.gradientRight)
              : styles.noGradient) + (className ? ' ' + className : '')
          }
          {...rest}
        >
          <div className={styles.container}>
            <Grid>
              <Row>
                <h3
                  className={styles.header + (visible ? '' : ' ' + styles.hide)}
                >
                  Frequently asked questions
                </h3>
              </Row>
              <PricingFAQSection
                leftTitle="How much will my project cost?"
                leftText="The cost of each project will vary depending on the type of freelancer you need, the experience level you require, and the project specification. Freelancers will pitch you quotes based on your project description."
                rightTitle="What payment methods do you accept?"
                rightText="We accept major credit and debit cards. Invoiced billing can be arranged on Twine Business or Enterprise accounts which can be paid by wire transfer, SEPA or Faster Payments (UK)."
              />
              <PricingFAQSection
                leftTitle="Can you hire a freelancer on a day rate or hourly rate?"
                leftText={
                  <span>
                    Yes, you can hire a freelancer on hourly or day rates. State
                    your preference within your project brief, and agree a rate
                    with your chosen freelancer. Your freelancer can then bill
                    you on Twine and attach a timesheet so you can see the hours
                    they worked.
                  </span>
                }
                rightTitle="How quickly can I hire someone?"
                rightText={
                  <span>
                    For popular projects like logos, simple graphic design and
                    music production you can expect freelancers to pitch in
                    within 24 hours. For higher demand roles, like developers or
                    UX designers, it may take over 24 hours before freelancers
                    start contacting you.
                    <br />
                    <br />
                    Urgent requirement?{' '}
                    <a
                      className="contact-support"
                      title="Speak to the team"
                      href="/contact"
                    >
                      Message the Twine team for help
                    </a>
                    .
                  </span>
                }
              />
              <PricingFAQSection
                leftTitle="What are the typical rates for freelancers?"
                leftText={
                  <span>
                    <a href="https://www.twine.net/blog/hire-freelancer-cost/">
                      Rates will vary
                    </a>{' '}
                    depending on the experience level you need. This is not a
                    definitive list but here are the ranges, from junior to
                    expert, that you would expect for a day’s work (7-8 hours):
                    <br />
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/graphic-designers">Graphic designer</a>:
                    $150-750
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/web-developers">Web developer</a>: $200-1000
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/ux-designers">UX designer</a>: $200-1000
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/videographers">Videographer</a>: $200-1000
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/animators">Animator</a>: $150-1000
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/digital-marketer">Digital Marketer</a>:
                    $150-750
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/illustrators">Illustrator</a>: $150-1000
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/copywriters">Copywriter</a>: $150-750
                    <br />
                    <span className="pl pr">&bull;</span>{' '}
                    <a href="/find/music-composers">Music producer</a>: $150-750
                    <br />
                    <br />
                    <a href="https://www.twine.net/blog/hire-freelancer-cost/">
                      Read more about how much a freelancer costs here
                    </a>
                    .
                  </span>
                }
                rightTitle="What is the Service fee?"
                rightText={
                  <span>
                    To help Twine run smoothly and to cover the costs of the
                    services we provide, such as 7 days a week customer support,
                    we charge a service fee when a hire is confirmed.
                    <br />
                    <br />
                    The service fee is a sliding fee based on the lifetime
                    payments to a freelancer on a project. The Service fee
                    reduces the more payments you make to a freelancer:
                    <br />
                    <br />
                    <span className="pl pr">&bull;</span> 10% on payments up to
                    $10,000
                    <br />
                    <span className="pl pr">&bull;</span> 5% on payments over
                    $10,000
                    <br />
                    <br />
                    For large projects see our{' '}
                    <Link to="/enterprise">Twine Enterprise offering</Link>.
                  </span>
                }
              />
              <PricingFAQSection
                leftTitle="How does payment work?"
                leftText="To secure a freelancer for work, you'll be asked to make an initial upfront payment. Either pay the full value of the project quoted by the freelancer, or agree a milestone schedule and pay smaller amounts incrementally."
                rightTitle="What if the project does not work out?"
                rightText="In the rare event a project doesn’t work out, we’ll refer to your agreed terms with the freelancer. If they’ve failed to deliver as promised, we’ll refund your upfront payment."
              />
            </Grid>
          </div>
        </div>
      </VisibilitySensor>
    );
  }
}
